import React, { useEffect, useMemo, useRef, useState } from 'react';
import { iaApi } from '../services/api';
import {
  GOAL_TIER_META,
  MAX_GOALS_PROFILE,
} from '../constants/goalTiers';
import {
  countGoalTiers,
  parseGoalTiers,
  removeGoalFromTier,
  toggleGoalInTier,
} from '../utils/trainingGoalTiers';
import GoalsPromptPreview from './GoalsPromptPreview';

function groupItemsByTier(categories) {
  const byTier = { essential: [], support: [], context: [] };
  for (const cat of categories || []) {
    for (const item of cat.items || []) {
      const tier = item.relevanceTier || 'context';
      if (!byTier[tier]) byTier[tier] = [];
      byTier[tier].push({
        ...item,
        categoryLabel: cat.label,
      });
    }
  }
  return byTier;
}

function TierDropdown({
  tierMeta,
  items,
  selected,
  totalCount,
  open,
  onToggleOpen,
  onToggleSlug,
  onRemoveSlug,
}) {
  const count = selected.length;
  const atMax = totalCount >= MAX_GOALS_PROFILE;

  return (
    <div
      className={`rounded-xl border p-3 ${
        tierMeta.id === 'essential'
          ? 'border-iron-accent/30 bg-iron-surface/40'
          : tierMeta.id === 'support'
            ? 'border-iron-muted-strong/35 bg-iron-surface/30'
            : 'border-iron-info/30 bg-iron-info/5'
      }`}
    >
      <div className="mb-2 flex items-start gap-2">
        <span
          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md font-mono text-[11px] font-semibold ${
            tierMeta.id === 'essential'
              ? 'bg-iron-accent/15 text-iron-accent'
              : tierMeta.id === 'support'
                ? 'bg-iron-muted-strong/15 text-iron-muted-strong'
                : 'bg-iron-info/12 text-iron-info'
          }`}
        >
          {tierMeta.rank}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-iron-text">{tierMeta.title}</p>
          <p className="text-[10px] text-iron-muted">{tierMeta.hint}</p>
        </div>
        <span className="rounded-full border border-iron-border px-2 py-0.5 font-mono text-[10px] text-iron-muted">
          {count}
        </span>
      </div>

      <div className="relative">
        <button
          type="button"
          onClick={onToggleOpen}
          className="flex w-full items-center justify-between gap-2 rounded-lg border border-iron-border bg-iron-surface px-3 py-2.5 text-left font-mono text-[11px] text-iron-muted-strong hover:border-iron-accent"
          aria-expanded={open}
        >
          <span>
            {count
              ? `${count} ${count === 1 ? 'objetivo selecionado' : 'objetivos selecionados'} · toque para editar`
              : tierMeta.triggerEmpty}
          </span>
          <span className="text-iron-accent">{open ? '▴' : '▾'}</span>
        </button>

        {open && (
          <div className="absolute left-0 right-0 top-[calc(100%+6px)] z-30 max-h-[min(50vh,280px)] overflow-y-auto rounded-xl border border-iron-border bg-iron-card p-2 shadow-xl">
            {items.length === 0 ? (
              <p className="px-2 py-3 font-mono text-[10px] text-iron-muted">Nenhum objetivo neste nível.</p>
            ) : (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {items.map((item) => {
                  const isSelected = selected.includes(item.slug);
                  const disabled = !isSelected && atMax;
                  return (
                    <button
                      key={item.slug}
                      type="button"
                      disabled={disabled}
                      onClick={() => onToggleSlug(item.slug)}
                      className={`flex flex-col items-start rounded-lg border px-3 py-2 text-left transition-colors ${
                        isSelected
                          ? 'border-iron-accent bg-iron-accent/10'
                          : disabled
                            ? 'border-iron-border opacity-35'
                            : 'border-iron-border bg-iron-surface hover:border-iron-accent'
                      }`}
                    >
                      <span className="text-[11px] font-semibold">{item.label}</span>
                      {item.description ? (
                        <span className="mt-1 text-[9px] text-iron-muted">{item.description}</span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {count > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {selected.map((slug) => {
            const item = items.find((i) => i.slug === slug);
            return (
              <span
                key={slug}
                className="inline-flex items-center gap-1 rounded-full border border-iron-border bg-iron-surface px-2 py-1 font-mono text-[9px] text-iron-text"
              >
                {item?.label || slug}
                <button
                  type="button"
                  onClick={() => onRemoveSlug(slug)}
                  className="text-iron-muted hover:text-red-400"
                  aria-label={`Remover ${item?.label || slug}`}
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function TrainingGoalsTierPicker({ value, onChange, max = MAX_GOALS_PROFILE }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [openTier, setOpenTier] = useState('essential');
  const rootRef = useRef(null);

  const tiers = useMemo(() => parseGoalTiers(value), [value]);
  const total = countGoalTiers(tiers);
  const atMax = total >= max;

  const isSlugSelected = (slug) =>
    (tiers.essential || []).includes(slug) ||
    (tiers.support || []).includes(slug) ||
    (tiers.context || []).includes(slug);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr('');
      try {
        const res = await iaApi.getTrainingObjectives();
        if (!cancelled) setCategories(res.data?.categories || []);
      } catch (e) {
        if (!cancelled) {
          setErr(e.response?.data?.error || e.message || 'Não foi possível carregar objetivos.');
          setCategories([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpenTier(null);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  const itemsByTier = useMemo(() => groupItemsByTier(categories), [categories]);

  const featured = useMemo(() => {
    const list = [];
    for (const cat of categories) {
      for (const item of cat.items || []) {
        if (item.featured) {
          list.push({
            ...item,
            categoryLabel: cat.label,
            relevanceTier: item.relevanceTier || 'context',
          });
        }
      }
    }
    return list;
  }, [categories]);

  const lookup = useMemo(() => {
    const map = {};
    for (const cat of categories) {
      for (const item of cat.items || []) {
        map[item.slug] = item;
      }
    }
    return map;
  }, [categories]);

  const handleToggle = (tierId, slug) => {
    onChange?.(toggleGoalInTier(tiers, tierId, slug, max));
  };

  const handleRemove = (tierId, slug) => {
    onChange?.(removeGoalFromTier(tiers, tierId, slug));
  };

  const chipClass = (on, disabled) =>
    `rounded-lg border px-3 py-2 font-mono text-xs transition-colors ${
      on
        ? 'border-iron-accent bg-iron-accent text-black font-medium'
        : disabled
          ? 'border-iron-border text-iron-muted opacity-40 pointer-events-none'
          : 'border-iron-border text-iron-muted hover:border-iron-accent hover:text-iron-accent'
    }`;

  return (
    <div ref={rootRef} className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <label className="section-label block">Objetivos</label>
        <span className="font-mono text-[10px] text-iron-muted">
          {total}/{max}
        </span>
      </div>
      <p className="font-mono text-[10px] text-iron-muted">
        3 níveis de relevância · até {max} no total · essencial pesa mais no prompt
      </p>

      {total >= max && (
        <p className="font-mono text-[10px] text-iron-accent">
          Limite de {max} objetivos atingido — remove um para escolher outro.
        </p>
      )}

      {err && <p className="text-xs text-iron-danger">{err}</p>}

      {loading && !categories.length ? (
        <p className="font-mono text-xs text-iron-muted">Carregando objetivos…</p>
      ) : (
        <div className="space-y-3">
          {featured.length > 0 && (
            <div>
              <p className="mb-2 font-mono text-[10px] uppercase tracking-wider text-iron-muted">
                Mais escolhidos
              </p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {featured.map((item) => (
                  <button
                    key={item.slug}
                    type="button"
                    title={item.description}
                    onClick={() => handleToggle(item.relevanceTier, item.slug)}
                    className={chipClass(
                      isSlugSelected(item.slug),
                      !isSlugSelected(item.slug) && atMax
                    )}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          {GOAL_TIER_META.map((meta) => (
            <TierDropdown
              key={meta.id}
              tierMeta={meta}
              items={itemsByTier[meta.id] || []}
              selected={tiers[meta.id] || []}
              totalCount={total}
              open={openTier === meta.id}
              onToggleOpen={() => setOpenTier((cur) => (cur === meta.id ? null : meta.id))}
              onToggleSlug={(slug) => handleToggle(meta.id, slug)}
              onRemoveSlug={(slug) => handleRemove(meta.id, slug)}
            />
          ))}
        </div>
      )}

      <GoalsPromptPreview tiers={tiers} lookup={lookup} />
    </div>
  );
}
